import { useMemo } from 'react'

import {
  CompositeTableTemplate,
  renderCellGrayZeros,
} from 'app/common/templates-next/table-template'

import {
  ColumnKey,
  cellsProps,
  columnKeysLeft,
  columnKeysMain,
  getHeaderCells,
  headerCellsProps,
  totalCellsProps,
} from './tableColumns'

import {
  ItemByStudentsType,
  TotalsFinancesByStudentsType,
} from 'app/dataTypes/reports/data/common/financesSummary'

import { useCurrencySign } from 'app/common/components-next/currency'
import { renderTableCell } from './renderTableCell'

type Props = {
  data: Array<ItemByStudentsType>
  totals: TotalsFinancesByStudentsType
  pending: boolean
}

export const TableGroupByStudents = ({ data, totals, pending }: Props) => {
  const currency = useCurrencySign()
  const tableData = useMemo(() => {
    return {
      columnKeysLeft,
      columnKeysMain,
      header: getHeaderCells(currency),
      headerProps: headerCellsProps,
      data: data.map(
        (
          {
            studentName,
            studentKlass,
            balanceBefore,
            paymentsResult,
            correctionPaymentsResult,
            writeOffs,
            balanceAfter,
          },
          index,
        ) => ({
          rowKey: studentName + studentKlass,
          rowData: {
            [ColumnKey.StudentName]: {
              columnKey: ColumnKey.StudentName as const,
              lastRow: index === data.length - 1,
              value: studentName,
            },
            [ColumnKey.StudentKlass]: {
              columnKey: ColumnKey.StudentKlass as const,
              value: studentKlass,
            },
            [ColumnKey.BalanceBefore]: {
              columnKey: ColumnKey.BalanceBefore as const,
              value: balanceBefore,
              isRedNegative: true,
            },
            [ColumnKey.PaymentsResult]: {
              columnKey: ColumnKey.PaymentsResult as const,
              value: paymentsResult,
            },
            [ColumnKey.CorrectionPaymentsResult]: {
              columnKey: ColumnKey.CorrectionPaymentsResult as const,
              value: correctionPaymentsResult,
            },
            [ColumnKey.WriteOffs]: {
              columnKey: ColumnKey.WriteOffs as const,
              value: writeOffs,
            },
            [ColumnKey.BalanceAfter]: {
              columnKey: ColumnKey.BalanceAfter as const,
              value: balanceAfter,
              isRedNegative: true,
            },
          },
        }),
      ),
      dataProps: cellsProps,
      total: [
        {
          rowKey: 'total',
          rowData: {
            [ColumnKey.StudentName]: {
              columnKey: ColumnKey.StudentName as const,
              value: 'ИТОГО',
            },
            [ColumnKey.StudentKlass]: {
              columnKey: ColumnKey.StudentKlass as const,
              value: '',
            },
            [ColumnKey.BalanceBefore]: {
              columnKey: ColumnKey.BalanceBefore as const,
              value: totals.balanceBefore,
              isRedNegative: true,
            },
            [ColumnKey.PaymentsResult]: {
              columnKey: ColumnKey.PaymentsResult as const,
              value: totals.payments,
            },
            [ColumnKey.CorrectionPaymentsResult]: {
              columnKey: ColumnKey.CorrectionPaymentsResult as const,
              value: totals.corrections,
            },
            [ColumnKey.WriteOffs]: {
              columnKey: ColumnKey.WriteOffs as const,
              value: totals.writeOffs,
            },
            [ColumnKey.BalanceAfter]: {
              columnKey: ColumnKey.BalanceAfter as const,
              value: totals.balanceAfter,
              isRedNegative: true,
            },
          },
        },
      ],
      totalProps: totalCellsProps,
    }
  }, [data, currency, totals])

  return (
    <CompositeTableTemplate
      data={tableData}
      loading={pending}
      renderCell={renderTableCell}
      renderFooterCell={renderCellGrayZeros}
      borderTop
    />
  )
}
